/**
 * Data Operations - Sync-aware wrappers for database operations
 *
 * All write operations go through these helpers to ensure
 * sync status is properly tracked.
 */

import {
  getDb,
  generateId,
  type SyncStatus,
  type SyncableLogEntry,
  type SyncablePeriod,
  type SyncableUserSettings,
  type SyncableCgmReading,
  type SyncableDayNote,
} from "../db/user-database";
import { syncService } from "../services/sync";
import type {
  LogEntry,
  Period,
  UserSettings,
  CgmReading,
  DayNote,
} from "../types";

/**
 * Remove sync metadata from settings before sending to server.
 * This prevents internal fields from being stored in the server data.
 */
function cleanSettingsForServer(
  settings: Omit<UserSettings, "id"> | SyncableUserSettings,
): Omit<UserSettings, "id"> {
  const cleaned = { ...settings } as Record<string, unknown>;
  delete cleaned.id;
  delete cleaned._syncStatus;
  delete cleaned._serverVersion;
  delete cleaned._localVersion;
  delete cleaned._deletedAt;
  delete cleaned._conflictData;
  delete cleaned._conflictAt;
  return cleaned as Omit<UserSettings, "id">;
}

// --- Archive Boundary Check ---

/**
 * Check if a timestamp falls within an archived month.
 * Archived data is read-only — edits must be rejected at the UI level.
 */
export async function isDateArchived(timestamp: Date): Promise<boolean> {
  try {
    const db = getDb();
    const meta = await db.archiveMetadata.get("state");
    if (!meta?.activeDataSince) return false;

    const boundary = new Date(meta.activeDataSince);
    return timestamp < boundary;
  } catch {
    return false;
  }
}

// --- Entry Operations ---

export async function saveEntry(
  entry: Omit<LogEntry, "id" | "createdAt" | "updatedAt"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const now = new Date();
  const id = entry.id || generateId();

  // Reject writes to archived months
  if (entry.timestamp && (await isDateArchived(entry.timestamp))) {
    throw new Error(
      "This entry falls within an archived month and cannot be modified.",
    );
  }

  // Preserve existing sync metadata if updating
  const existing = entry.id ? await db.entries.get(entry.id) : undefined;

  const syncableEntry: SyncableLogEntry = {
    ...entry,
    id,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.entries.put(syncableEntry);
  syncService.scheduleSyncSoon();

  return id;
}

export async function updateEntry(
  id: string,
  updates: Partial<LogEntry>,
): Promise<void> {
  const db = getDb();
  const existing = await db.entries.get(id);

  if (existing) {
    // Reject edits to archived entries
    if (existing.timestamp && (await isDateArchived(existing.timestamp))) {
      throw new Error(
        "This entry falls within an archived month and cannot be modified.",
      );
    }

    await db.entries.update(id, {
      ...updates,
      updatedAt: new Date(),
      _syncStatus: "pending" as SyncStatus,
      _localVersion: Date.now(),
      // _serverVersion preserved by update() - not replaced
    });
    syncService.scheduleSyncSoon();
  }
}

export async function deleteEntry(id: string): Promise<void> {
  const db = getDb();
  const existing = await db.entries.get(id);

  if (existing) {
    if (existing.timestamp && (await isDateArchived(existing.timestamp))) {
      throw new Error(
        "This entry falls within an archived month and cannot be deleted.",
      );
    }

    // Soft delete for sync
    await db.entries.update(id, {
      _syncStatus: "pending" as SyncStatus,
      _deletedAt: new Date().toISOString(),
      _localVersion: Date.now(),
    });
    syncService.scheduleSyncSoon();
  }
}

export async function getEntry(id: string): Promise<LogEntry | undefined> {
  const db = getDb();
  const entry = await db.entries.get(id);
  return entry && !entry._deletedAt ? entry : undefined;
}

export async function getAllEntries(): Promise<LogEntry[]> {
  const db = getDb();
  const entries = await db.entries.toArray();
  return entries.filter((e) => !e._deletedAt);
}

export async function getEntriesByPeriod(
  periodTag: string,
): Promise<LogEntry[]> {
  const db = getDb();
  const entries = await db.entries
    .where("periodTag")
    .equals(periodTag)
    .toArray();
  return entries.filter((e) => !e._deletedAt);
}

export async function getEntriesInDateRange(
  startDate: Date,
  endDate: Date,
): Promise<LogEntry[]> {
  const db = getDb();
  const startTs = startDate.getTime();
  const endTs = endDate.getTime();

  // Helper to get timestamp as number (handles string/Date inconsistency)
  const getTs = (t: Date | string | number | unknown): number => {
    if (t instanceof Date) return t.getTime();
    if (typeof t === "string") return new Date(t).getTime();
    if (typeof t === "number") return t;
    return new Date(t as string).getTime();
  };

  const entries = await db.entries.toArray();
  return entries.filter((e) => {
    if (e._deletedAt) return false;
    const ts = getTs(e.timestamp);
    return ts >= startTs && ts <= endTs;
  });
}

// --- Period Operations ---

export async function savePeriod(
  period: Omit<Period, "id"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const id = period.id || generateId();

  // Preserve existing sync metadata if updating
  const existing = period.id ? await db.periods.get(period.id) : undefined;

  const syncablePeriod: SyncablePeriod = {
    ...period,
    id,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.periods.put(syncablePeriod);
  syncService.scheduleSyncSoon();

  return id;
}

export async function getAllPeriods(): Promise<Period[]> {
  const db = getDb();
  const periods = await db.periods.orderBy("sortOrder").toArray();
  return periods.filter((p) => !p._deletedAt);
}

export async function getPeriod(id: string): Promise<Period | undefined> {
  const db = getDb();
  const period = await db.periods.get(id);
  return period && !period._deletedAt ? period : undefined;
}

// --- Settings Operations ---

/**
 * Custom error thrown when writes are blocked due to sync protection.
 * This prevents data loss on new devices before initial sync completes.
 */
export class SyncProtectionError extends Error {
  constructor(reason: string) {
    super(
      `Cannot save: ${reason}. Please wait for sync to complete or go online.`,
    );
    this.name = "SyncProtectionError";
  }
}

export interface SaveSettingsResult {
  success: boolean;
  conflict: boolean; // True if server had different data (server won)
  serverData?: UserSettings; // The server's data if conflict occurred
  savedData?: UserSettings; // The saved data with new version (on success)
  offline: boolean; // True if saved offline (pending sync)
}

export async function saveSettings(
  settings: Omit<UserSettings, "id">,
): Promise<SaveSettingsResult> {
  const db = getDb();

  // Get current local settings to check version
  const existing = await db.settings.get("settings");
  const currentVersion = existing?._serverVersion ?? 0;

  // Try to save to server first (optimistic concurrency)
  if (navigator.onLine) {
    try {
      const { saveSettingsToServer } = await import("../services/settings-api");

      // Clean sync metadata before sending to server
      const cleanedSettings = cleanSettingsForServer(settings);
      const result = await saveSettingsToServer(
        cleanedSettings,
        currentVersion,
      );

      // Server accepted - save locally with new version
      const syncableSettings: SyncableUserSettings = {
        ...settings,
        id: "settings",
        _syncStatus: "synced",
        _serverVersion: result.version,
      };
      await db.settings.put(syncableSettings);
      console.log(`[Settings] Saved to server, version: ${result.version}`);
      return {
        success: true,
        conflict: false,
        offline: false,
        savedData: syncableSettings as UserSettings,
      };
    } catch (error) {
      // Check for version conflict
      if (
        error instanceof Error &&
        error.name === "SettingsVersionConflictError"
      ) {
        const conflictError = error as unknown as {
          serverData: UserSettings;
          serverVersion: number;
        };
        console.warn(
          `[Settings] Version conflict! Local: ${currentVersion}, Server: ${conflictError.serverVersion}`,
        );

        // Server always wins - accept server data
        if (conflictError.serverData) {
          const syncableSettings: SyncableUserSettings = {
            ...conflictError.serverData,
            id: "settings",
            _syncStatus: "synced",
            _serverVersion: conflictError.serverVersion,
          };
          await db.settings.put(syncableSettings);
          console.log(
            `[Settings] Server won - saved server data, version: ${conflictError.serverVersion}`,
          );
        }

        return {
          success: false,
          conflict: true,
          serverData: conflictError.serverData,
          offline: false,
        };
      }
      // Network error - fall through to offline save
      console.warn("[Settings] Server save failed, saving locally:", error);
    }
  }

  // Offline or server failed - save locally as pending
  const syncableSettings: SyncableUserSettings = {
    ...settings,
    id: "settings",
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };
  await db.settings.put(syncableSettings);
  syncService.scheduleSyncSoon();
  return {
    success: true,
    conflict: false,
    offline: true,
    savedData: syncableSettings as UserSettings,
  };
}

export async function getSettings(): Promise<UserSettings | undefined> {
  const db = getDb();
  return await db.settings.get("settings");
}

// --- CGM Reading Operations ---

export async function saveCgmReading(
  reading: Omit<CgmReading, "id"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const id = reading.id || generateId();

  // Preserve existing sync metadata if updating
  const existing = reading.id
    ? await db.cgmReadings.get(reading.id)
    : undefined;

  const syncableReading: SyncableCgmReading = {
    ...reading,
    id,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.cgmReadings.put(syncableReading);
  syncService.scheduleSyncSoon();

  return id;
}

export async function saveCgmReadingsBatch(
  readings: Array<Omit<CgmReading, "id"> & { id?: string }>,
): Promise<string[]> {
  const db = getDb();
  const now = Date.now();

  // Get existing records to check if data changed
  const idsToCheck = readings.map((r) => r.id).filter(Boolean) as string[];
  const existingRecords =
    idsToCheck.length > 0 ? await db.cgmReadings.bulkGet(idsToCheck) : [];
  const existingMap = new Map(
    existingRecords
      .filter((r): r is SyncableCgmReading => r !== undefined)
      .map((r) => [r.id, r]),
  );

  // Only include readings that are new or have changed data
  const readingsToSave: SyncableCgmReading[] = [];
  const unchangedIds: string[] = [];

  for (const reading of readings) {
    const id = reading.id || generateId();
    const existing = existingMap.get(id);

    if (existing) {
      // Check if data has actually changed (compare key fields)
      const timestampMatch =
        new Date(existing.timestamp).getTime() ===
        new Date(reading.timestamp).getTime();
      const glucoseMatch = existing.glucose === reading.glucose;
      const typeMatch = existing.type === reading.type;
      const sourceMatch = existing.source === reading.source;

      if (timestampMatch && glucoseMatch && typeMatch && sourceMatch) {
        // Data unchanged - skip this reading entirely
        unchangedIds.push(id);
        continue;
      }
    }

    // New or changed reading - mark for sync
    readingsToSave.push({
      ...reading,
      id,
      _syncStatus: "pending" as SyncStatus,
      _localVersion: now,
      _serverVersion: existing?._serverVersion,
    });
  }

  if (readingsToSave.length > 0) {
    await db.cgmReadings.bulkPut(readingsToSave);
    syncService.scheduleSyncSoon();
  }

  // Return all IDs (both saved and unchanged)
  return [...readingsToSave.map((r) => r.id), ...unchangedIds];
}

export async function getCgmReadingsInDateRange(
  startDate: Date,
  endDate: Date,
): Promise<CgmReading[]> {
  const db = getDb();
  const startTs = startDate.getTime();
  const endTs = endDate.getTime();

  // Helper to get timestamp as number (handles string/Date inconsistency)
  const getTs = (t: Date | string | number | unknown): number => {
    if (t instanceof Date) return t.getTime();
    if (typeof t === "string") return new Date(t).getTime();
    if (typeof t === "number") return t;
    return new Date(t as string).getTime();
  };

  const readings = await db.cgmReadings.toArray();
  return readings.filter((r) => {
    if (r._deletedAt) return false;
    const ts = getTs(r.timestamp);
    return ts >= startTs && ts <= endTs;
  });
}

export async function getLatestCgmReading(): Promise<CgmReading | undefined> {
  const db = getDb();
  const reading = await db.cgmReadings.orderBy("timestamp").last();
  return reading && !reading._deletedAt ? reading : undefined;
}

// --- Day Note Operations ---

export async function saveDayNote(
  note: Omit<DayNote, "id" | "createdAt" | "updatedAt"> & { id?: string },
): Promise<string> {
  const db = getDb();
  const now = new Date();
  const id = note.id || generateId();

  // Preserve existing sync metadata if updating
  const existing = note.id ? await db.dayNotes.get(note.id) : undefined;

  const syncableNote: SyncableDayNote = {
    ...note,
    id,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    _syncStatus: "pending",
    _localVersion: Date.now(),
    _serverVersion: existing?._serverVersion,
  };

  await db.dayNotes.put(syncableNote);
  syncService.scheduleSyncSoon();

  return id;
}

export async function updateDayNote(
  id: string,
  updates: Partial<DayNote>,
): Promise<void> {
  const db = getDb();
  const existing = await db.dayNotes.get(id);

  if (existing) {
    await db.dayNotes.update(id, {
      ...updates,
      updatedAt: new Date(),
      _syncStatus: "pending" as SyncStatus,
      _localVersion: Date.now(),
    });
    syncService.scheduleSyncSoon();
  }
}

export async function deleteDayNote(id: string): Promise<void> {
  const db = getDb();
  const existing = await db.dayNotes.get(id);

  if (existing) {
    // Soft delete for sync
    await db.dayNotes.update(id, {
      _syncStatus: "pending" as SyncStatus,
      _deletedAt: new Date().toISOString(),
      _localVersion: Date.now(),
    });
    syncService.scheduleSyncSoon();
  }
}

export async function getDayNote(id: string): Promise<DayNote | undefined> {
  const db = getDb();
  const note = await db.dayNotes.get(id);
  return note && !note._deletedAt ? note : undefined;
}

export async function getDayNotesForDate(date: string): Promise<DayNote[]> {
  const db = getDb();
  const notes = await db.dayNotes.where("date").equals(date).toArray();
  return notes.filter((n) => !n._deletedAt);
}

export async function getDayNotesInDateRange(
  startDate: string,
  endDate: string,
): Promise<DayNote[]> {
  const db = getDb();
  const notes = await db.dayNotes
    .where("date")
    .between(startDate, endDate, true, true)
    .toArray();
  return notes.filter((n) => !n._deletedAt);
}

export async function getAllDayNotes(): Promise<DayNote[]> {
  const db = getDb();
  const notes = await db.dayNotes.toArray();
  return notes.filter((n) => !n._deletedAt);
}

// --- Sync Status Queries ---

export async function getPendingChangesCount(): Promise<number> {
  const db = getDb();
  let count = 0;

  count += await db.entries.where("_syncStatus").equals("pending").count();
  count += await db.periods.where("_syncStatus").equals("pending").count();
  count += await db.settings.where("_syncStatus").equals("pending").count();
  count += await db.cgmReadings.where("_syncStatus").equals("pending").count();

  return count;
}

export async function getConflictsCount(): Promise<number> {
  const db = getDb();
  let count = 0;

  count += await db.entries.where("_syncStatus").equals("conflict").count();
  count += await db.periods.where("_syncStatus").equals("conflict").count();
  count += await db.settings.where("_syncStatus").equals("conflict").count();
  count += await db.cgmReadings.where("_syncStatus").equals("conflict").count();

  return count;
}

export async function resolveConflict(
  table: "entries" | "periods" | "settings" | "cgmReadings",
  id: string,
  keepLocal: boolean,
): Promise<void> {
  const db = getDb();

  let item: { _syncStatus?: string } | undefined;

  if (table === "entries") {
    item = await db.entries.get(id);
  } else if (table === "periods") {
    item = await db.periods.get(id);
  } else if (table === "settings") {
    item = await db.settings.get("settings");
  } else if (table === "cgmReadings") {
    item = await db.cgmReadings.get(id);
  }

  if (!item || item._syncStatus !== "conflict") return;

  const updateData = keepLocal
    ? {
        _syncStatus: "pending" as SyncStatus,
        _conflictData: undefined,
        _conflictAt: undefined,
        _localVersion: Date.now(),
      }
    : {
        _syncStatus: "synced" as SyncStatus,
        _conflictData: undefined,
        _conflictAt: undefined,
      };

  if (table === "entries") {
    await db.entries.update(id, updateData);
  } else if (table === "periods") {
    await db.periods.update(id, updateData);
  } else if (table === "settings") {
    await db.settings.update("settings", updateData);
  } else if (table === "cgmReadings") {
    await db.cgmReadings.update(id, updateData);
  }

  if (keepLocal) {
    syncService.scheduleSyncSoon();
  }
}
